<div class="om-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'objectMappings.pageTitle' | translate">
    <p subtitle class="om-subtitle" data-testid="om-subtitle">
      {{ 'objectMappings.pageSubtitle' | translate }}
      <code class="om-code">{{ 'objectMappings.pageSubtitleCode' | translate }}</code>
    </p>
    <ng-container right>
      @if (!loading() && !loadError()) {
        <app-stat-group
          data-testid="om-stats"
          [items]="[
            {
              label: 'objectMappings.stats.elements' | translate,
              value: elementCount(),
              testId: 'om-stat-elements',
            },
            {
              label: 'objectMappings.stats.objects' | translate,
              value: objectCount(),
              testId: 'om-stat-objects',
            },
          ]"
        />
      }
      @if (!isReadOnly()) {
        <button mat-flat-button type="button" data-testid="om-create" (click)="creating.set(true)">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'objectMappings.create.button' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="om-toolbar">
    <app-search-bar
      class="om-search"
      data-testid="om-search"
      [placeholder]="'objectMappings.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    @if (!loading() && !loadError()) {
      <app-tag-filter
        data-testid="om-tag-filter"
        [tags]="tagOptions()"
        [value]="selectedTags()"
        [ariaLabel]="'objectMappings.tagFilterLabel' | translate"
        (valueChange)="onTagsChange($event)"
      />
    }
    <app-saved-views-menu
      data-testid="om-saved-views"
      [views]="savedViewsOptions()"
      [activeName]="activeSavedViewName()"
      [context]="'OBJECT_MAPPINGS'"
      [projectId]="projectId"
      (viewSelected)="onSavedViewSelected($event)"
      (viewSaved)="onSavedViewSaved($event)"
      (viewDeleted)="onSavedViewDeleted($event)"
      (viewDeleteFailed)="onSavedViewDeleteFailed()"
    />
  </div>

  @if (savedViewsError(); as viewsError) {
    <p class="om-hint om-hint--error" role="alert" data-testid="om-views-error">{{ viewsError }}</p>
  }

  @if (loading()) {
    <div class="om-loading" data-testid="om-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <div class="om-hint om-hint--error" role="alert" data-testid="om-error">
      <p>{{ 'objectMappings.loadError' | translate }}</p>
      <button mat-stroked-button type="button" data-testid="om-retry" (click)="load()">
        {{ 'objectMappings.retry' | translate }}
      </button>
    </div>
  } @else {
    <div class="om-card" role="table" [attr.aria-label]="'objectMappings.tableLabel' | translate">
      <div class="om-table-header" role="row">
        <div class="om-col om-col--name" role="columnheader">{{ 'objectMappings.table.name' | translate }}</div>
        <div class="om-col om-col--tech" role="columnheader">{{ 'objectMappings.table.technology' | translate }}</div>
        <div class="om-col om-col--address" role="columnheader">{{ 'objectMappings.table.address' | translate }}</div>
        <div class="om-col om-col--objects" role="columnheader">
          {{ 'objectMappings.table.entryCount' | translate }}
        </div>
        <div class="om-col om-col--state" role="columnheader">{{ 'objectMappings.state.column' | translate }}</div>
        <div class="om-col om-col--updated" role="columnheader">{{ 'objectMappings.table.updated' | translate }}</div>
      </div>
      @if (libraries().length === 0) {
        <p class="om-empty" data-testid="om-empty">{{ 'objectMappings.empty' | translate }}</p>
      } @else if (filtered().length === 0) {
        <p class="om-empty" data-testid="om-empty-filtered">{{ 'objectMappings.emptyFiltered' | translate }}</p>
      }
      @for (library of filtered(); track library.id) {
        <div
          class="om-row"
          role="row"
          tabindex="0"
          data-testid="om-row"
          [attr.aria-label]="library.name"
          (click)="open(library)"
          (keydown.enter)="open(library)"
        >
          <div class="om-col om-col--name" role="cell">
            <app-icon class="om-name-icon" name="library" [size]="16" />
            <code class="om-name">{{ library.name }}</code>
          </div>
          <div class="om-col om-col--tech" role="cell">
            <span class="om-tech" data-testid="om-tech">
              @if (technologyOf(library.engineId); as techKey) {
                {{ techKey | translate }}
              } @else {
                {{ library.engineId }}
              }
            </span>
          </div>
          <div
            class="om-col om-col--address om-address"
            role="cell"
            data-testid="om-address"
            [matTooltip]="library.address ?? ''"
            [matTooltipDisabled]="!library.address"
          >
            {{ library.address }}
          </div>
          <div class="om-col om-col--objects" role="cell" data-testid="om-objects">{{ library.entryCount }}</div>
          <div class="om-col om-col--state" role="cell" data-testid="om-state">
            @let libState = stateOf(library);
            @if (libState.kind; as kind) {
              <app-status-pill
                class="om-state-pill"
                [kind]="kind"
                [label]="libState.key | translate: { count: libState.count }"
              />
            } @else {
              <span class="om-state-empty">{{ libState.key | translate }}</span>
            }
          </div>
          <div class="om-col om-col--updated" role="cell" data-testid="om-updated">
            {{ library.updatedAt | date: 'dd.MM.yyyy' }}
          </div>
        </div>
      }
    </div>
  }
</div>

@if (creating()) {
  <app-object-mapping-create-dialog
    [projectId]="projectId"
    [existingNames]="existingNames()"
    (created)="onCreated($event)"
    (cancelled)="creating.set(false)"
  />
}
